import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, PanelLeftClose, PanelLeft, Search, FolderKanban, Megaphone, BarChart3, Settings } from "lucide-react";
import {
  listConversations,
  getActiveId,
  setActiveId,
  newConversation,
  deleteConversation,
  subscribe,
  syncFromServer,
} from "../lib/conversationStore";
import * as API from "../api";

// ===========================================================================
// ChatMenu (Menu B) — left-docked conversation panel.
//
// Shows ONLY on the chat page (active === "talkToVella"). Collapsible to 0px
// so the chat surface takes the full width. Collapsed state persists to
// localStorage. Fully independent from TopPagesMenu (Menu A).
//
// Recents come from conversationStore. Switching / creating a conversation
// fires `vella:conversationSwitch` — the same event TalkToVella listens for.
// Styled to the Cowork reference: Pinned + Recents sections, active row with a
// 2px ocean left border, and a pinned user-profile footer.
// ===========================================================================

const FONT = '-apple-system, BlinkMacSystemFont, "Inter", system-ui, sans-serif';
const PANEL_W = 280;
const COLLAPSE_KEY = "vella_chatmenu_collapsed";

// Quick-nav items → App.jsx routes (via the vella:navigate event it listens for).
// Projects maps to the existing creative-studio workspace (there is no dedicated
// "projects" route). The other three routes exist as-is.
const NAV_ITEMS = [
  { label: "Projects", route: "creative-studio", Icon: FolderKanban },
  { label: "Campaigns", route: "ad-campaigns", Icon: Megaphone },
  { label: "Analytics", route: "performance", Icon: BarChart3 },
  { label: "Settings", route: "settings", Icon: Settings },
];

const C = {
  border: "#dfe2e3",
  hover: "#f7f8f8",
  row: "#565c5e",
  name: "#181b1c",
  section: "#9ba1a3",
  ocean: "#275c73",
  activeBg: "#eef3f5",
  surface: "#ffffff",
};

const STYLE_ID = "chat-menu-css";
const CSS = `
.cm-panel { transition: width 280ms ease-out; }
.cm-convo:hover { background: ${C.hover}; }
.cm-convo:hover .cm-del { opacity: 1; }
.cm-newchat:hover { background: ${C.activeBg}; }
.cm-iconbtn:hover { background: ${C.hover}; }
@media (prefers-reduced-motion: reduce) { .cm-panel { transition: none !important; } }
`;

export function getChatMenuCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
}

function SectionHeader({ children }) {
  return (
    <div style={{ padding: "10px 16px 4px" }}>
      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em", color: C.section }}>{children}</span>
    </div>
  );
}

function ConvoRow({ c, on, onSelect, onDelete }) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="cm-convo"
      onClick={() => onSelect(c.id)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(c.id); } }}
      style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "8px 16px", cursor: "pointer",
        // Reserve the 2px border in both states so the active row doesn't shift.
        borderLeft: `2px solid ${on ? C.ocean : "transparent"}`,
        background: on ? C.activeBg : "transparent",
      }}
    >
      <span style={{ flex: 1, minWidth: 0, fontFamily: FONT, fontSize: 13, fontWeight: on ? 500 : 400, color: on ? C.ocean : C.row, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {c.title || "New conversation"}
      </span>
      <button
        type="button"
        className="cm-del"
        aria-label="Delete conversation"
        onClick={(e) => onDelete(e, c.id)}
        style={{ opacity: 0, width: 24, height: 24, flexShrink: 0, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "transparent", border: "none", color: C.section, transition: "opacity 140ms ease" }}
      >
        <Trash2 size={14} strokeWidth={1.6} />
      </button>
    </div>
  );
}

export default function ChatMenu({ collapsed, setCollapsed, user }) {
  const [convos, setConvos] = useState(() => listConversations());
  const [activeConvo, setActiveConvo] = useState(() => getActiveId());
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);

  const refresh = useCallback(() => {
    setConvos(listConversations());
    setActiveConvo(getActiveId());
  }, []);
  useEffect(() => subscribe(refresh), [refresh]);

  // Pull any server-side conversations into the local cache on mount so the
  // Recents list reflects chats created on other devices/sessions — not just
  // this browser's localStorage. Best-effort: syncFromServer resolves false
  // (and we skip the refresh) when the backend endpoint is absent. The store's
  // merge always preserves local message history, so switching a past chat
  // still hydrates its full thread.
  useEffect(() => {
    let alive = true;
    syncFromServer(API).then((didSync) => { if (alive && didSync) refresh(); }).catch(() => {});
    return () => { alive = false; };
  }, [refresh]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  }, [setCollapsed]);

  const selectConvo = useCallback((id) => {
    setActiveId(id);
    setActiveConvo(id);
    window.dispatchEvent(new CustomEvent("vella:conversationSwitch", { detail: { id } }));
  }, []);

  const handleNewChat = useCallback(() => {
    const id = newConversation();
    API.createConversation?.({ id }).catch(() => {});
    setActiveId(id);
    setActiveConvo(id);
    window.dispatchEvent(new CustomEvent("vella:conversationSwitch", { detail: { id } }));
  }, []);

  const handleDelete = useCallback((e, id) => {
    e.stopPropagation();
    deleteConversation(id);
    API.deleteConversationRemote?.(id).catch(() => {});
    refresh();
  }, [refresh]);

  const go = useCallback((route) => {
    window.dispatchEvent(new CustomEvent("vella:navigate", { detail: route }));
  }, []);

  // Collapsed: a thin floating reopen button so the user can bring it back.
  // High z-index so the content column (which docks flush against this 0-width
  // rail under the parent's overflow:hidden) can't cover it — that cover was
  // why the toggle "wouldn't reopen".
  if (collapsed) {
    return (
      <div className="cm-panel" style={{ width: 0, flexShrink: 0, position: "relative" }}>
        <button
          type="button"
          aria-label="Open chat menu"
          onClick={toggleCollapsed}
          style={{
            position: "absolute", top: 12, left: 8, zIndex: 30,
            width: 34, height: 34, borderRadius: 9, display: "flex",
            alignItems: "center", justifyContent: "center", cursor: "pointer",
            background: C.surface, border: `1px solid ${C.border}`, color: C.section,
            boxShadow: "0 2px 8px -4px rgba(39,92,115,0.2)",
          }}
        >
          <PanelLeft size={17} strokeWidth={1.6} />
        </button>
      </div>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q ? convos.filter((c) => (c.title || "").toLowerCase().includes(q)) : convos;
  const pinned = filtered.filter((c) => c.pinned);
  const recents = filtered.filter((c) => !c.pinned);

  const userName = (user?.name || user?.email?.split("@")[0] || "You").trim();
  const initial = (userName.charAt(0) || "V").toUpperCase();
  const planLabel = user?.plan || user?.subscription?.plan || null;
  const secondary = planLabel ? `${planLabel} plan` : (user?.email || "");

  return (
    <aside
      className="cm-panel"
      aria-label="Conversations"
      style={{
        width: PANEL_W, flexShrink: 0, height: "100%",
        background: C.surface, borderRight: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}
    >
      {/* Top — collapse toggle + search */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 12px 8px" }}>
        <button
          type="button"
          className="cm-iconbtn"
          aria-label="Collapse chat menu"
          onClick={toggleCollapsed}
          style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: "transparent", border: "none", color: C.section }}
        >
          <PanelLeftClose size={17} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          className="cm-iconbtn"
          aria-label="Search chats"
          onClick={() => setShowSearch((s) => { if (s) setQuery(""); return !s; })}
          style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: showSearch ? C.hover : "transparent", border: "none", color: C.section }}
        >
          <Search size={16} strokeWidth={1.7} />
        </button>
      </div>

      {showSearch && (
        <div style={{ padding: "0 12px 8px" }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats"
            style={{ width: "100%", boxSizing: "border-box", fontFamily: FONT, fontSize: 13, padding: "8px 12px", borderRadius: 9, border: `1px solid ${C.border}`, outline: "none", color: C.name, background: C.surface }}
          />
        </div>
      )}

      {/* New chat */}
      <div style={{ padding: "0 16px 12px" }}>
        <button
          type="button"
          className="cm-newchat"
          onClick={handleNewChat}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", height: 36, borderRadius: 10, cursor: "pointer",
            background: "transparent", border: `1px solid ${C.ocean}`, color: C.ocean,
            fontFamily: FONT, fontSize: 13, fontWeight: 500,
          }}
        >
          <Plus size={16} strokeWidth={1.8} /> New chat
        </button>
      </div>

      {/* Quick nav */}
      <nav style={{ padding: "0 8px 8px", marginBottom: 4, borderBottom: `1px solid ${C.border}` }}>
        {NAV_ITEMS.map((it) => (
          <button
            key={it.route}
            type="button"
            className="cm-convo"
            onClick={() => go(it.route)}
            style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "8px 12px", borderRadius: 7, cursor: "pointer", background: "transparent", border: "none", fontFamily: FONT, fontSize: 13, fontWeight: 400, color: C.row, textAlign: "left" }}
          >
            <it.Icon size={16} strokeWidth={1.7} style={{ flexShrink: 0, color: C.section }} />
            {it.label}
          </button>
        ))}
      </nav>

      {/* Scrollable list — Pinned (if any) then Recents */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 12 }}>
        {pinned.length === 0 && recents.length === 0 && (
          <p style={{ fontFamily: FONT, fontSize: 12, color: C.section, padding: "8px 16px" }}>
            {q ? "No chats match your search." : "No conversations yet."}
          </p>
        )}

        {pinned.length > 0 && (
          <>
            <SectionHeader>Pinned</SectionHeader>
            {pinned.map((c) => (
              <ConvoRow key={c.id} c={c} on={c.id === activeConvo} onSelect={selectConvo} onDelete={handleDelete} />
            ))}
          </>
        )}

        {recents.length > 0 && (
          <>
            <SectionHeader>Recents</SectionHeader>
            {recents.map((c) => (
              <ConvoRow key={c.id} c={c} on={c.id === activeConvo} onSelect={selectConvo} onDelete={handleDelete} />
            ))}
          </>
        )}
      </div>

      {/* Bottom — user profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.ocean, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT, fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
          {initial}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: C.name, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userName}</div>
          {secondary && (
            <div style={{ fontFamily: FONT, fontSize: 11, color: C.section, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{secondary}</div>
          )}
        </div>
      </div>
    </aside>
  );
}
